import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronRight, GitBranch, User, UserPlus } from 'lucide-react';
import type { CalendarEvent, CalendarQuickOutcome } from '../../lib/types';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useCalendarSharingStore } from '../../lib/stores/calendarSharingStore';
import { applyQuickOutcome, outcomesForType } from '../../lib/calendar/quickOutcome';
import { outcomeColor } from '../../lib/calendar/outcomeLabel';
import { supabase } from '../../lib/supabase';
import { HelpTip } from '../ui/HelpTip';
import { PropRow } from '../shared/activity-drawer/ui/PropRow';
import { DrawerCallout } from '../shared/activity-drawer/ui/DrawerCallout';
import { useApp } from '../AppProvider';

const AFTER_TEXT: Record<CalendarQuickOutcome['after'], string> = {
  none: '',
  callback: 'Crea un richiamo per chi l\'ha fissato, il giorno lavorativo dopo alle 10',
  reschedule: '',
  offer: 'Dopo la conferma puoi creare l\'offerta dalla scheda del lead',
};

/**
 * Nel dettaglio di una task commerciale: di chi è il calendario, chi l'ha fissata e "Com'è andata?"
 * con i pulsanti di esito impostati dall'azienda.
 */
export function SalesActivityQuickOutcome({ event, canEdit, onDone, onReschedule }: {
  event: CalendarEvent;
  canEdit: boolean;
  onDone: () => void;
  onReschedule?: () => void;
}) {
  const md = (event.metadata || {}) as Record<string, any>;
  const { toast } = useApp();
  const settings = useSettingsStore(s => s.settings);
  const { people, ctx } = useCalendarSharingStore();
  const [picked, setPicked] = useState<CalendarQuickOutcome | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [stageNames, setStageNames] = useState<Record<string, string>>({});

  const outcomes = useMemo(() => outcomesForType(settings?.calendar_quick_outcomes, md.activity_type), [settings?.calendar_quick_outcomes, md.activity_type]);
  const nameOf = (id?: string | null) => (id ? people.find(p => p.id === id)?.name : undefined);
  const ownerName = nameOf(md.owner_id);
  const creatorName = nameOf(md.creator_id);

  useEffect(() => {
    const ids = outcomes.map(o => o.stage_id).filter(Boolean) as string[];
    if (!ids.length) return;
    void supabase.from('lead_pipeline_stages').select('id, name').in('id', ids).then(({ data }) => {
      setStageNames(Object.fromEntries((data || []).map((s: any) => [s.id, s.name])));
    });
  }, [outcomes]);

  useEffect(() => { setPicked(null); setNote(''); }, [event.id]);

  const confirm = async () => {
    if (!picked || !ctx) return;
    if (picked.note_required && !note.trim()) { toast.warning(`La nota è obbligatoria per "${picked.label}"`); return; }
    setSaving(true);
    try {
      const r = await applyQuickOutcome({
        activityId: event.original_id || String(event.id).replace(/^sales_activity_/, ''),
        outcome: picked, note, leadId: md.lead_id, creatorId: md.creator_id, meId: ctx.meId,
        organizationId: ctx.organizationId, activityTitle: event.title, activityStart: event.start_time,
      });
      toast.success(r.stageMoved ? `Esito "${picked.label}" salvato, lead spostato` : `Esito "${picked.label}" salvato`);
      if (r.stageSkippedReason) toast.warning(`Fase non cambiata: ${r.stageSkippedReason}`);
      // Rimandato: si apre subito la modifica per scegliere il nuovo orario (il calendario si ricarica al salvataggio)
      if (picked.after === 'reschedule' && onReschedule) onReschedule();
      else onDone();
    } catch (e: any) {
      toast.error(e?.message || 'Esito non salvato');
    } finally {
      setSaving(false);
    }
  };

  const ic = 'h-3 w-3';
  // Esito in evidenza: etichetta leggera nel colore dell'esito (quello impostato in Esiti, se c'e')
  const pillColor = md.outcome ? outcomeColor(md.outcome, settings?.calendar_quick_outcomes, !!md.is_completed) : null;
  const stageColor = typeof md.lead_stage_color === 'string' && /^#[0-9a-fA-F]{6}$/.test(md.lead_stage_color) ? md.lead_stage_color : '#787774';
  return (
    <div className="px-3 pb-2">
      {md.outcome && pillColor && (
        <div className="px-1.5 pb-2" data-testid="event-outcome-pill">
          <span
            className="inline-flex max-w-full items-start gap-1.5 rounded-md px-2 py-1 text-[13px] font-medium leading-snug"
            style={{ backgroundColor: `${pillColor}1F`, color: pillColor }}
          >
            <CheckCircle2 className="mt-[2px] h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
            <span className="min-w-0 break-words">{md.is_completed ? '' : 'Esito (ancora aperta): '}{md.outcome}</span>
          </span>
        </div>
      )}
      {ownerName && <PropRow icon={<User className={ic} strokeWidth={1.5} />} label="Calendario di"><span>{ownerName}</span></PropRow>}
      {creatorName && md.creator_id !== md.owner_id && (
        <PropRow icon={<UserPlus className={ic} strokeWidth={1.5} />} label="Fissato da"><span>{creatorName}</span></PropRow>
      )}
      {md.lead_id && md.lead_stage_name && (
        <PropRow icon={<GitBranch className={ic} strokeWidth={1.5} />} label="Fase del lead">
          <span className="inline-flex min-w-0 items-center gap-1.5" data-testid="event-lead-stage">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: stageColor }} />
            <span className="truncate">{String(md.lead_stage_name)}</span>
          </span>
        </PropRow>
      )}

      {canEdit && !md.is_completed && outcomes.length > 0 && (
        <div className="mt-3 border-t border-[rgba(55,53,47,0.09)] px-1.5 pt-3 dark:border-white/10">
          <div className="mb-1 flex items-center gap-1.5 text-[13px] font-medium text-[#37352F] dark:text-white">
            Com'è andata?
            <HelpTip
              title="Esito con un click"
              text="Scegli com'è andato l'appuntamento: si chiude, l'esito resta scritto e il lead passa nella fase collegata. Gli esiti e le fasi li imposta l'amministratore in Impostazioni › Calendario › Esiti."
              example='"Assente": l&apos;appuntamento si chiude, il lead va in fase Assente e chi l&apos;aveva fissato trova un richiamo per il giorno dopo.'
            />
          </div>
          {!picked ? (
            <div className="flex flex-col">
              {outcomes.map(o => (
                <button key={o.id} type="button" onClick={() => setPicked(o)}
                  className="group flex min-h-[30px] items-center gap-2 rounded px-1.5 py-0.5 text-left hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06]">
                  <span className="flex-1 text-[13px] text-[#37352F] dark:text-white">{o.label}</span>
                  {o.stage_id && stageNames[o.stage_id] && <span className="text-[11px] text-[#787774]">fase {stageNames[o.stage_id]}</span>}
                  <ChevronRight className="h-3.5 w-3.5 text-[#787774] opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100" strokeWidth={1.5} />
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="px-1.5 text-[13px] text-[#37352F] dark:text-white">Esito: <span className="font-medium">{picked.label}</span></div>
              <div>
                <label htmlFor="quick-outcome-note" className="mb-1 block px-1.5 text-[12px] text-[#787774] dark:text-gray-400">
                  Nota {picked.note_required ? <span className="text-[#C4554D]">obbligatoria per {picked.label}</span> : '(facoltativa)'}
                </label>
                <textarea
                  id="quick-outcome-note"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={3}
                  autoFocus
                  className="block w-full resize-y rounded-md border border-[rgba(55,53,47,0.09)] bg-[#F7F7F5] px-2.5 py-1.5 text-[13px] text-[#37352F] placeholder:text-[#9B9A97] focus:border-[#2383E2] focus:outline-none focus:ring-1 focus:ring-[#2383E2] dark:border-[#3F4447] dark:bg-[#373C3F] dark:text-white"
                />
              </div>
              <DrawerCallout>
                <div className="mb-0.5 font-medium">Cosa succede con Conferma</div>
                <div>{picked.after === 'reschedule' ? `L'appuntamento resta aperto con esito ${picked.label}: scegli il nuovo orario` : `L'appuntamento si chiude con esito ${picked.label}`}</div>
                {picked.stage_id && md.lead_id && <div>Il lead passa in fase {stageNames[picked.stage_id] || 'collegata'}</div>}
                {/* Sempre visibile: se comparisse scrivendo, sposterebbe il pulsante Conferma sotto al mouse */}
                {md.lead_id && <div>Se scrivi una nota, va nella scheda del lead</div>}
                {AFTER_TEXT[picked.after] && <div>{AFTER_TEXT[picked.after]}</div>}
              </DrawerCallout>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setPicked(null)} className="rounded-md px-3 py-1.5 text-[13px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)]">Indietro</button>
                <button type="button" disabled={saving} onClick={() => void confirm()} className="rounded-md bg-[#37352F] px-3 py-1.5 text-[13px] font-medium text-white hover:bg-black disabled:opacity-50">Conferma esito</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Evento del CRM messo nel calendario di un collega ("Calendario di", metadata.calendar_owner_id): nel dettaglio
 * le stesse righe "Calendario di" e "Fissato da" delle task commerciali.
 */
export function EventOwnerRows({ event }: { event: CalendarEvent }) {
  const { people } = useCalendarSharingStore();
  const ownerId = (event.metadata as any)?.calendar_owner_id as string | undefined;
  const nameOf = (id?: string | null) => (id ? people.find(p => p.id === id)?.name : undefined);
  const ownerName = nameOf(ownerId);
  const creatorName = nameOf(event.created_by);
  if (!ownerName) return null;
  const ic = 'h-3 w-3';
  return (
    <div className="px-3 pb-2">
      <PropRow icon={<User className={ic} strokeWidth={1.5} />} label="Calendario di"><span>{ownerName}</span></PropRow>
      {creatorName && event.created_by !== ownerId && (
        <PropRow icon={<UserPlus className={ic} strokeWidth={1.5} />} label="Fissato da"><span>{creatorName}</span></PropRow>
      )}
    </div>
  );
}
